import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as Highcharts from 'highcharts';
import NetworkgraphModule from 'highcharts/modules/networkgraph';
import { Subscription } from 'rxjs';
import dayjs from 'dayjs';

import { DaterangePickerComponent } from '../../components/date-range-picker/date-range-picker.component';
import { TimezoneComponent } from '../../components/timezone/timezone.component';
import { CommonService } from '../../services/common.service';
import { SharedDataService } from '../../services/shared-data.service';
import { environment } from '../../../environments/environment';
import {
  buildDummyClusterGraph,
  buildDummyClusterReviewItems,
  buildDummyExtract,
  buildDummyRelated,
  buildDummyReviewConversations,
} from './freeform-collection-dummy.data';

NetworkgraphModule(Highcharts);

export type FreeformFieldKey =
  | 'Topics'
  | 'RootCause'
  | 'CustomerPainPoint'
  | 'Trigger'
  | 'Outcome'
  | 'Intent'
  | 'PrimaryTopic'
  | 'Sentiment'
  | 'JourneyMoment'
  | 'CallerType'
  | 'Resolution';
export type FreeformViewMode = 'free_form' | 'clustered' | 'mapping';
export type ExtractMode = 'all' | 'unique';
export type WorkspaceTab =
  | 'extract'
  | 'cluster-review'
  | 'related'
  | 'cluster-map';
export interface FreeformValueCount {
  value: string;
  count: number;
}

export interface FreeformClusterMapping {
  cluster_value: string;
  free_form_value: string;
  count: number;
}

export interface FreeformFieldExtract {
  free_form: FreeformValueCount[];
  clustered: FreeformValueCount[];
  mapping: FreeformClusterMapping[];
  free_form_total?: number | null;
  clustered_total?: number | null;
  mapping_total?: number | null;
}

export interface FreeformCollectionExtractResponse {
  start_date: string;
  end_date: string;
  timeZone: string;
  mode: ExtractMode;
  total_conversations: number;
  fields?: Partial<Record<FreeformFieldKey, FreeformFieldExtract>> | null;
  rows?: Record<string, string | number>[] | null;
  row_count?: number | null;
  truncated?: boolean | null;
  page?: number | null;
  page_size?: number | null;
  total_rows?: number | null;
  has_more?: boolean | null;
}

export interface RelatedMatch {
  field: FreeformFieldKey;
  field_label: string;
  layer: string;
  value: string;
  count: number;
  score: number;
  matched_tokens: string[];
}

export interface RelatedResponse {
  query: string;
  tokens: string[];
  matches: RelatedMatch[];
  by_field: Partial<Record<FreeformFieldKey, RelatedMatch[]>>;
  charts: {
    packedbubble: any[];
    wordcloud: { name: string; weight: number; field: string }[];
  };
}


export interface ClusterGraphNode {
  id: string;
  name: string;
  type: 'cluster' | 'free_form';
  field: FreeformFieldKey;
  field_label: string;
  count: number;
  cluster?: string;
  color?: string;
  marker?: { radius: number };
}

export interface ClusterGraphLink {
  from: string;
  to: string;
  weight: number;
  field: FreeformFieldKey;
}

export interface NewClusterCandidate {
  field: FreeformFieldKey;
  field_label: string;
  current_cluster: string;
  free_form_value: string;
  count: number;
  share_of_cluster: number;
  lexical_overlap: number;
  reasons: string[];
  suggested_cluster_name: string;
  priority_score: number;
}

export interface SuggestedClusterGroup {
  field: FreeformFieldKey;
  field_label: string;
  suggested_cluster_name: string;
  anchor_token: string;
  members: { free_form_value: string; count: number; current_cluster: string }[];
  total_count: number;
  member_count: number;
}

export interface ClusterGraphResponse {
  start_date: string;
  end_date: string;
  timeZone: string;
  fields: Partial<Record<FreeformFieldKey, any>>;
  graph: { nodes: ClusterGraphNode[]; links: ClusterGraphLink[] };
  new_cluster_candidates: NewClusterCandidate[];
  suggested_cluster_groups: SuggestedClusterGroup[];
  summary: {
    cluster_nodes: number;
    free_form_nodes: number;
    edges: number;
    candidates: number;
  };
}

export interface ClusterReviewClosest {
  cluster_id: number;
  cluster_name: string;
  value_text: string;
  similarity: number;
  similarity_pct: number;
  mechanism?: string;
}

export interface ClusterReviewRecommendation {
  action: string;
  similarity_pct: number | null;
  label: string;
  detail: string;
  allow_create_cluster: boolean;
  allow_mark_other: boolean;
  allow_reassign: boolean;
}

export interface ClusterReviewItem {
  field: FreeformFieldKey;
  field_label: string;
  free_form_value: string;
  count: number;
  assigned_cluster_name: string;
  predicted_cluster_name?: string;
  best_similarity: number | null;
  similarity_pct: number | null;
  closest_clusters: ClusterReviewClosest[];
  recommendation?: ClusterReviewRecommendation;
  decision?: any;
  compare_error?: string;
}

export type ReviewBucket =
  | 'all'
  | 'other'
  | 'auto_assign'
  | 'human_review'
  | 'create_new_cluster'
  | 'mark_other'
  | 'needs_compare'
  | 'open_decisions';

const FIELD_TABS: { key: FreeformFieldKey; label: string; hasClustered: boolean }[] = [
  { key: 'Topics', label: 'Topics', hasClustered: true },
  { key: 'RootCause', label: 'Root Cause', hasClustered: true },
  { key: 'CustomerPainPoint', label: 'Pain Point', hasClustered: true },
  { key: 'Trigger', label: 'Trigger', hasClustered: true },
  { key: 'Outcome', label: 'Outcome', hasClustered: true },
  // Closed taxonomy — not embedding clusters (Intent lives with Primary Topic, not cluster catalog)
  { key: 'PrimaryTopic', label: 'Primary Topic', hasClustered: false },
  { key: 'Intent', label: 'Intent', hasClustered: false },
  { key: 'Sentiment', label: 'Sentiment', hasClustered: false },
  { key: 'JourneyMoment', label: 'Journey Moment', hasClustered: false },
  { key: 'CallerType', label: 'Caller Type', hasClustered: false },
  { key: 'Resolution', label: 'Resolution', hasClustered: false },
];

/** Free-form / clustered fields for Related and Cluster map. */
const PAIRED_FIELD_TABS = FIELD_TABS.filter((t) => t.hasClustered);

/** Closed taxonomy fields shown in Extract (browse only). */
const TAXONOMY_FIELD_TABS = FIELD_TABS.filter((t) => !t.hasClustered);

/**
 * Cluster review is embedding-catalog HITL for Topics/TRPO only.
 * Intent and other closed taxonomy fields are not edited here.
 */
const CLUSTER_REVIEW_FIELD_TABS = PAIRED_FIELD_TABS;

@Component({
  selector: 'app-freeform-collection-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
    DaterangePickerComponent,
    TimezoneComponent,
  ],
  providers: [CommonService, SharedDataService],
  templateUrl: './freeform-collection-dashboard.component.html',
  styleUrl: './freeform-collection-dashboard.component.scss',
})
export class FreeformCollectionDashboardComponent implements OnInit, OnDestroy {
  readonly fieldTabs = FIELD_TABS;
  readonly pairedFieldTabs = PAIRED_FIELD_TABS;
  readonly taxonomyFieldTabs = TAXONOMY_FIELD_TABS;
  readonly clusterReviewFieldTabs = CLUSTER_REVIEW_FIELD_TABS;

  workspaceTab: WorkspaceTab = 'extract';
  /** Default: day before yesterday → yesterday (today’s ingest may still be incomplete). */
  startDate = dayjs().subtract(2, 'day').format('YYYY-MM-DD');
  endDate = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
  readonly dateRangeSyncTooltip =
    'Sync one calendar day at a time at production volume (~15k conversations/day).\n\n' +
    'Default is yesterday and the day before — today’s ingest may still be incomplete.\n\n' +
    'Limitation: a multi-day Load or closeness re-score can time out, saturate the database pool, and hit Bedrock embed limits.\n\n' +
    'Advantage: the per-day score cache and coverage calendar stay accurate, requests stay fast, and you can retry a single day if something fails.';
  isLoading = false;
  errorMessage = '';
  searchTerm = '';

  // Extract
  extractMode: ExtractMode = 'unique';
  /** Which field(s) to request from the extract API (speeds up response). */
  extractFieldFilter: FreeformFieldKey | 'ALL' = 'Topics';
  activeField: FreeformFieldKey = 'Topics';
  activeView: FreeformViewMode = 'free_form';
  extractResult: FreeformCollectionExtractResponse | null = null;
  extractPage = 1;
  readonly extractPageSize = 100;
  extractDownloadBusy = false;

  // Related NL
  relatedQuery = '';
  relatedResult: RelatedResponse | null = null;

  // Cluster map
  clusterGraphResult: ClusterGraphResponse | null = null;
  clusterMapField: FreeformFieldKey | 'ALL' = 'ALL';
  networkChartOptions: Highcharts.Options = {};
  updateNetworkFlag = false;

  // Cluster review (HITL)
  reviewBucket: ReviewBucket = 'other';
  reviewFieldFilter: FreeformFieldKey | 'ALL' = 'ALL';
  reviewItems: ClusterReviewItem[] = [];
  reviewPolicy: any = null;
  reviewIncludeScores = true;
  activeReviewItem: ClusterReviewItem | null = null;
  reviewClusterOptions: { cluster_id: number; cluster_name: string; mechanism: string }[] = [];
  reviewSelectedClusterName = '';
  reviewNewClusterName = '';
  reviewActionMessage = '';
  reviewActionMode: 'assign' | 'create' | 'other' = 'assign';
  reviewLastCreatedCluster: string | null = null;
  reviewConversations: any[] = [];
  reviewConversationsTotal = 0;
  reviewConversationsTruncated = false;
  selectedConversationKeys: Record<string, boolean> = {};
  reviewApplyToUpcoming = true;
  reviewWindowDays = 0;
  reviewMinCount: number | null = null;
  reviewPerformanceNote = '';
  /** Date windows previously closeness-scored (from DB coverage table). */
  reviewScoreCoverage: Array<{
    field: string;
    field_label?: string;
    bucket: string;
    start_date: string;
    end_date: string;
    values_total: number;
    values_from_cache: number;
    values_newly_embedded: number;
    values_failed: number;
    status: string;
    scored_at?: string;
  }> = [];
  reviewThisWindowCoverage: Array<{
    field: string;
    field_label?: string;
    bucket: string;
    start_date: string;
    end_date: string;
    values_total: number;
    values_from_cache: number;
    values_newly_embedded: number;
    values_failed: number;
    status: string;
    scored_at?: string;
  }> = [];
  /** Per-day coverage map date → { status, fields } for calendar ticks. */
  reviewCalendarDays: Record<string, { date: string; status: string; fields: string[] }> = {};
  reviewCalendarMonth: dayjs.Dayjs = dayjs().startOf('month');
  reviewCalendarSelectedDay: string | null = null;

  // Dev E2E seed: transcript → Bedrock → RDS → Insights + Freeform live APIs
  testDataScenarios: { id: string; label: string; hitl_targets?: string[]; turns?: number }[] = [];
  selectedTestScenarioIds: Record<string, boolean> = {};
  testDataCountPerScenario = 2;
  testDataDaysBack = 7;
  testDataUseBedrock = false;
  testDataGenerating = false;
  testDataMessage = '';
  testDataCreated: Array<{
    conversation_id: string;
    contact_id: string;
    scenario_id: string;
    label: string;
    extraction_mode?: string;
  }> = [];
  testDataHowTo: string[] = [];
  /** After a successful seed (or when dummy is off), hit live APIs. */
  preferLiveApiAfterSeed = false;

  /** Right-side drawer: help guide or E2E seed (mutually exclusive). */
  rightDrawer: 'none' | 'help' | 'seed' | 'coverage' = 'none';
  helpFocus:
    | 'overview'
    | 'extract'
    | 'cluster-review'
    | 'related' = 'overview';

  // Download options panel
  showDownloadOptions = false;
  downloadFields: Record<FreeformFieldKey, boolean> = {
    Topics: true,
    RootCause: true,
    CustomerPainPoint: true,
    Trigger: true,
    Outcome: true,
    Intent: true,
    PrimaryTopic: true,
    Sentiment: true,
    JourneyMoment: true,
    CallerType: true,
    Resolution: true,
  };
  downloadLayers = {
    free_form: true,
    clustered: false,
    mapping: false,
  };
  downloadScope: 'selection' | 'current_view' = 'current_view';

  private timezoneChangeSub?: Subscription;
  /** Remember which queue row to re-open after refresh / page reload. */
  private pendingReviewSelectionKey: string | null = null;
  private static readonly WORKSPACE_STATE_KEY = 'freeform-collection-workspace-v1';

  constructor(
    private commonService: CommonService,
    private sharedDataService: SharedDataService
  ) {}

  ngOnInit(): void {
    this.initializeDefaultDateRange();
    const restored = this.restoreWorkspaceState();
    this.timezoneChangeSub = this.sharedDataService.getTimezoneChange().subscribe(() => {
      this.refreshActiveWorkspace();
    });
    if (this.showDevTestDataTools) {
      this.loadTestDataScenarios();
    }
    // After restore, reload the active tab so Cluster review / fields stay put on browser refresh.
    if (restored) {
      this.refreshActiveWorkspace();
    }
  }

  ngOnDestroy(): void {
    this.persistWorkspaceState();
    this.timezoneChangeSub?.unsubscribe();
  }

  /** Persist tab + review filters so browser refresh does not jump back to Extract. */
  persistWorkspaceState(): void {
    try {
      const activeKey = this.activeReviewItem
        ? `${this.activeReviewItem.field}||${this.activeReviewItem.free_form_value}`
        : this.pendingReviewSelectionKey;
      const payload = {
        workspaceTab: this.workspaceTab,
        startDate: this.startDate,
        endDate: this.endDate,
        reviewBucket: this.reviewBucket,
        reviewFieldFilter: this.reviewFieldFilter,
        reviewIncludeScores: this.reviewIncludeScores,
        activeReviewKey: activeKey,
        extractMode: this.extractMode,
        extractFieldFilter: this.extractFieldFilter,
        activeField: this.activeField,
        activeView: this.activeView,
        searchTerm: this.searchTerm,
        extractPage: this.extractPage,
      };
      sessionStorage.setItem(
        FreeformCollectionDashboardComponent.WORKSPACE_STATE_KEY,
        JSON.stringify(payload)
      );
    } catch {
      /* ignore quota / private mode */
    }
  }

  restoreWorkspaceState(): boolean {
    try {
      const raw = sessionStorage.getItem(
        FreeformCollectionDashboardComponent.WORKSPACE_STATE_KEY
      );
      if (!raw) return false;
      const s = JSON.parse(raw);
      const tabs: WorkspaceTab[] = [
        'extract',
        'cluster-review',
        'related',
        'cluster-map',
      ];
      if (s.workspaceTab === 'trends') this.workspaceTab = 'extract';
      else if (tabs.includes(s.workspaceTab)) this.workspaceTab = s.workspaceTab;
      if (s.startDate) this.startDate = s.startDate;
      if (s.endDate) this.endDate = s.endDate;
      if (s.reviewBucket) this.reviewBucket = s.reviewBucket;
      if (s.reviewFieldFilter) this.reviewFieldFilter = s.reviewFieldFilter;
      if (typeof s.reviewIncludeScores === 'boolean') {
        this.reviewIncludeScores = s.reviewIncludeScores;
      }
      if (s.activeReviewKey) this.pendingReviewSelectionKey = s.activeReviewKey;
      if (s.extractMode === 'unique' || s.extractMode === 'all') {
        this.extractMode = s.extractMode;
      }
      if (s.extractFieldFilter === 'ALL' || this.fieldTabs.some((t) => t.key === s.extractFieldFilter)) {
        this.extractFieldFilter = s.extractFieldFilter;
      }
      if (s.activeField) this.activeField = s.activeField;
      if (s.activeView) this.activeView = s.activeView;
      if (!this.fieldHasClustered(this.activeField) && this.activeView !== 'free_form') {
        this.activeView = 'free_form';
      }
      if (typeof s.searchTerm === 'string') this.searchTerm = s.searchTerm;
      if (typeof s.extractPage === 'number' && s.extractPage >= 1) {
        this.extractPage = s.extractPage;
      }
      this.helpFocus = this.workspaceTab === 'cluster-map' ? 'cluster-review' : this.workspaceTab;
      return true;
    } catch {
      return false;
    }
  }

  reviewItemKey(item: { field: string; free_form_value: string }): string {
    return `${item.field}||${item.free_form_value}`;
  }

  initializeDefaultDateRange(): void {
    const yesterday = dayjs().subtract(1, 'day');
    this.endDate = yesterday.format('YYYY-MM-DD');
    this.startDate = yesterday.subtract(1, 'day').format('YYYY-MM-DD');
  }

  get primaryActionLabel(): string {
    switch (this.workspaceTab) {
      case 'related':
        return 'Find related';
      case 'cluster-map':
        return 'Build map';
      case 'cluster-review':
        return 'Load review';
      default:
        return 'Extract';
    }
  }

  get workspaceTabLabel(): string {
    switch (this.workspaceTab) {
      case 'extract':
        return 'Extract';
      case 'related':
        return 'Related';
      case 'cluster-map':
        return 'Cluster map';
      case 'cluster-review':
        return 'Cluster review';
      default:
        return 'Workspace';
    }
  }

  get workspaceTabHint(): string {
    switch (this.workspaceTab) {
      case 'extract':
        return 'Pull unique or row-level free-form values for the date range';
      case 'related':
        return 'Find related free-form / cluster values from a short description';
      case 'cluster-map':
        return 'Inspect cluster ↔ free-form structure and new candidates';
      case 'cluster-review':
        return 'Read conversations, then assign or create cluster labels only';
      default:
        return '';
    }
  }

  get primaryActionIcon(): string {
    switch (this.workspaceTab) {
      case 'related':
        return 'search';
      case 'cluster-map':
        return 'hub';
      case 'cluster-review':
        return 'rule';
      default:
        return 'play_arrow';
    }
  }

  /** Download is available only on Extract after a successful extract. */
  get canDownload(): boolean {
    return this.workspaceTab === 'extract' && !!this.extractResult;
  }

  onDateRangeSelect(event: any): void {
    if (event?.startDate && event?.endDate) {
      this.startDate = event.startDate.format('YYYY-MM-DD');
      this.endDate = event.endDate.format('YYYY-MM-DD');
      // Clear stale results so Conversation count cannot linger from a previous range
      this.extractResult = null;
      this.extractPage = 1;
      this.relatedResult = null;
      this.clusterGraphResult = null;
      this.reviewItems = [];
      this.activeReviewItem = null;
      this.persistWorkspaceState();
      this.refreshActiveWorkspace();
    }
  }

  setWorkspaceTab(tab: WorkspaceTab): void {
    this.workspaceTab = tab;
    this.errorMessage = '';
    this.helpFocus = tab === 'cluster-map' ? 'cluster-review' : tab;
    this.closeDownloadOptions();
    this.persistWorkspaceState();
    // Cluster review: open the score calendar in the right sidebar.
    if (tab === 'cluster-map' && !this.clusterGraphResult) this.runClusterGraph();
    if (tab === 'cluster-review') {
      this.rightDrawer = 'coverage';
      this.loadClusterReviewScoreCoverage();
      if (!this.reviewItems.length) this.loadClusterReview();
    }
  }

  setReviewBucket(bucket: ReviewBucket): void {
    this.reviewBucket = bucket;
    this.persistWorkspaceState();
    this.loadClusterReview({ preserveSelection: false });
  }

  setReviewFieldFilter(field: FreeformFieldKey | 'ALL'): void {
    this.reviewFieldFilter = field;
    this.persistWorkspaceState();
    this.loadClusterReviewScoreCoverage();
    this.loadClusterReview({ preserveSelection: false });
  }

  /** Reload queue after assign/create while keeping tab, filters, and selection when possible. */
  refreshClusterReview(): void {
    if (this.activeReviewItem) {
      this.pendingReviewSelectionKey = this.reviewItemKey(this.activeReviewItem);
    }
    this.persistWorkspaceState();
    this.loadClusterReview({ preserveSelection: true });
  }

  get helpOpen(): boolean {
    return this.rightDrawer === 'help';
  }

  get showTestDataPanel(): boolean {
    return this.rightDrawer === 'seed';
  }

  get coveragePanelOpen(): boolean {
    return this.rightDrawer === 'coverage';
  }

  openRightDrawer(kind: 'help' | 'seed' | 'coverage'): void {
    this.rightDrawer = this.rightDrawer === kind ? 'none' : kind;
    if (this.rightDrawer === 'seed' && !this.testDataScenarios.length) {
      this.loadTestDataScenarios();
    }
    if (this.rightDrawer === 'coverage') {
      this.loadClusterReviewScoreCoverage();
    }
  }

  closeRightDrawer(): void {
    this.rightDrawer = 'none';
  }

  @HostListener('document:keydown.escape')
  onEscapeCloseDrawer(): void {
    if (this.rightDrawer !== 'none') {
      this.closeRightDrawer();
    }
  }

  toggleHelp(): void {
    this.openRightDrawer('help');
  }

  toggleCoveragePanel(): void {
    this.openRightDrawer('coverage');
  }

  setHelpFocus(section: FreeformCollectionDashboardComponent['helpFocus']): void {
    this.helpFocus = section;
    this.rightDrawer = 'help';
  }

  toggleTestDataPanel(): void {
    this.openRightDrawer('seed');
  }

  setExtractMode(mode: ExtractMode): void {
    if (this.extractMode === mode) return;
    this.extractMode = mode;
    this.extractResult = null;
    this.extractPage = 1;
    this.closeDownloadOptions();
    this.persistWorkspaceState();
  }

  setExtractFieldFilter(field: FreeformFieldKey | 'ALL'): void {
    if (this.extractFieldFilter === field) return;
    this.extractFieldFilter = field;
    if (field !== 'ALL') {
      this.activeField = field;
      if (!this.fieldHasClustered(field) && this.activeView !== 'free_form') {
        this.activeView = 'free_form';
      }
    }
    this.extractResult = null;
    this.extractPage = 1;
    this.searchTerm = '';
    this.closeDownloadOptions();
    this.persistWorkspaceState();
  }

  /** Single field-pill row: avoid repeating the same chips for filter vs active field. */
  isExtractFieldPillActive(field: FreeformFieldKey): boolean {
    if (this.extractFieldFilter === 'ALL') {
      return this.extractResult?.mode === 'unique' && this.activeField === field;
    }
    return this.extractFieldFilter === field;
  }

  onExtractFieldPillClick(field: FreeformFieldKey): void {
    if (
      this.extractFieldFilter === 'ALL' &&
      this.extractResult?.mode === 'unique' &&
      this.extractResult.fields
    ) {
      if (this.activeField !== field) {
        this.setActiveField(field);
        return;
      }
      // Second click on the active field pins extract to that field only.
    }
    this.setExtractFieldFilter(field);
  }

  setActiveField(field: FreeformFieldKey): void {
    this.activeField = field;
    this.searchTerm = '';
    if (!this.fieldHasClustered(field) && this.activeView !== 'free_form') {
      this.activeView = 'free_form';
    }
    // When browsing a multi-field extract, stay on cached data; otherwise re-extract that field.
    if (this.extractFieldFilter !== 'ALL' && this.extractFieldFilter !== field) {
      this.extractFieldFilter = field;
      this.extractPage = 1;
      this.extract();
      return;
    }
    if (
      this.extractResult?.mode === 'unique' &&
      this.extractFieldFilter === 'ALL' &&
      !this.extractResult.fields?.[field]
    ) {
      this.extractFieldFilter = field;
      this.extractPage = 1;
      this.extract();
      return;
    }
    this.persistWorkspaceState();
    this.syncDownloadPanelToCurrentView();
  }

  fieldHasClustered(field: FreeformFieldKey = this.activeField): boolean {
    return !!this.fieldTabs.find((t) => t.key === field)?.hasClustered;
  }

  setActiveView(view: FreeformViewMode): void {
    this.activeView = view;
    this.searchTerm = '';
    // Remap page for the newly selected list (totals differ per layer).
    this.extractPage = 1;
    this.syncDownloadPanelToCurrentView();
    if (this.extractResult && this.extractResult.page_size) {
      this.extract();
    }
  }

  setExtractPage(page: number): void {
    const totalPages = this.extractTotalPages;
    if (page < 1 || page > totalPages || page === this.extractPage) return;
    this.extractPage = page;
    this.extract();
  }

  private dateBody() {
    return {
      start_date: this.startDate,
      end_date: this.endDate,
      timeZone: this.sharedDataService.selectedTimezoneValue || 'EST',
    };
  }

  refreshActiveWorkspace(): void {
    if (!this.startDate || !this.endDate) return;
    if (this.workspaceTab === 'extract') this.extract();
    if (this.workspaceTab === 'related' && this.relatedQuery.trim()) this.runRelated();
    if (this.workspaceTab === 'cluster-map') this.runClusterGraph();
    if (this.workspaceTab === 'cluster-review') this.loadClusterReview();
  }

  runPrimaryAction(): void {
    if (this.workspaceTab === 'extract') this.extract();
    else if (this.workspaceTab === 'related') this.runRelated();
    else if (this.workspaceTab === 'cluster-map') this.runClusterGraph();
    else if (this.workspaceTab === 'cluster-review') this.loadClusterReview();
  }

  extract(): void {
    if (!this.startDate || !this.endDate) {
      this.errorMessage = 'Please select a from and to date.';
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';

    if (this.useDummyData) {
      this.withDummyDelay(() => {
        this.extractResult = buildDummyExtract(
          this.startDate,
          this.endDate,
          this.sharedDataService.selectedTimezoneValue || 'EST',
          this.extractMode
        );
        this.isLoading = false;
      });
      return;
    }

    this.commonService
      .extractFreeformCollection(this.buildExtractRequestBody({ paginated: true }))
      .subscribe({
        next: (response: FreeformCollectionExtractResponse) => {
          this.extractResult = response;
          if (
            this.extractFieldFilter !== 'ALL' &&
            response.fields?.[this.extractFieldFilter]
          ) {
            this.activeField = this.extractFieldFilter;
          } else if (response.fields) {
            const keys = Object.keys(response.fields) as FreeformFieldKey[];
            if (keys.length && !response.fields[this.activeField]) {
              this.activeField = keys[0];
            }
          }
          this.isLoading = false;
          this.persistWorkspaceState();
        },
        error: (error) => this.handleError(error, 'Failed to extract freeform collection.'),
      });
  }

  /** Build extract API body. Browse uses page_size; download omits it for full values. */
  private buildExtractRequestBody(opts: {
    paginated: boolean;
    fields?: FreeformFieldKey[];
    mode?: ExtractMode;
  }): Record<string, unknown> {
    const fields =
      opts.fields ??
      (this.extractFieldFilter === 'ALL' ? undefined : [this.extractFieldFilter]);
    const body: Record<string, unknown> = {
      ...this.dateBody(),
      mode: opts.mode ?? this.extractMode,
      exclude_na: true,
      fields,
      // No free_form_limit / row_limit — return all values (paginated when browsing).
      free_form_limit: null,
      row_limit: null,
    };
    if (opts.paginated) {
      body['page'] = this.extractPage;
      body['page_size'] = this.extractPageSize;
    }
    return body;
  }

  runRelated(): void {
    if (!this.relatedQuery.trim()) {
      this.errorMessage = 'Enter a concept or scenario in natural language.';
      return;
    }
    if (!this.startDate || !this.endDate) {
      this.errorMessage = 'Please select a from and to date.';
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';

    if (this.useDummyData) {
      this.withDummyDelay(() => {
        this.relatedResult = buildDummyRelated(
          this.relatedQuery.trim(),
          this.startDate,
          this.endDate,
          this.sharedDataService.selectedTimezoneValue || 'EST'
        );
        this.isLoading = false;
      });
      return;
    }

    this.commonService
      .getFreeformRelated({
        ...this.dateBody(),
        query: this.relatedQuery.trim(),
        // No limit — return all scored matches; UI search filters locally.
        exclude_na: true,
      })
      .subscribe({
        next: (response: RelatedResponse) => {
          this.relatedResult = response;
          this.isLoading = false;
        },
        error: (error) => this.handleError(error, 'Failed to find related values.'),
      });
  }

  private get useDummyData(): boolean {
    if (this.preferLiveApiAfterSeed) return false;
    return !!(environment as { useFreeformDummyData?: boolean }).useFreeformDummyData;
  }

  /** Dev toolbar: E2E seed controls (transcript → Bedrock → RDS). */
  get showDevTestDataTools(): boolean {
    return !!(environment as { showFreeformE2ESeed?: boolean }).showFreeformE2ESeed;
  }

  get dataSourceLabel(): string {
    if (this.preferLiveApiAfterSeed || !this.useDummyData) return 'Live APIs (RDS)';
    return 'Static dummy (offline)';
  }

  private withDummyDelay(fn: () => void, ms = 250): void {
    setTimeout(fn, ms);
  }

  loadTestDataScenarios(): void {
    this.commonService.listFreeformTestDataScenarios().subscribe({
      next: (res: any) => {
        this.testDataScenarios = res?.scenarios || [];
        if (!Object.keys(this.selectedTestScenarioIds).length) {
          for (const s of this.testDataScenarios) {
            this.selectedTestScenarioIds[s.id] = true;
          }
        }
      },
      error: (error) => {
        this.testDataMessage =
          error?.error?.detail ||
          error?.message ||
          'Could not load scenarios. Backend must be running with FASTAPI_ENV=dev.';
      },
    });
  }

  toggleTestScenario(id: string): void {
    this.selectedTestScenarioIds[id] = !this.selectedTestScenarioIds[id];
  }

  selectAllTestScenarios(select: boolean): void {
    for (const s of this.testDataScenarios) {
      this.selectedTestScenarioIds[s.id] = select;
    }
  }

  get selectedTestScenarioIdList(): string[] {
    return this.testDataScenarios
      .filter((s) => this.selectedTestScenarioIds[s.id])
      .map((s) => s.id);
  }

  /** Full E2E: sample transcripts → Bedrock extract → RDS → refresh Freeform from live APIs. */
  generateBedrockTestData(): void {
    const scenarioIds = this.selectedTestScenarioIdList;
    if (this.testDataScenarios.length && !scenarioIds.length) {
      this.errorMessage = 'Select at least one scenario to generate.';
      return;
    }

    this.testDataGenerating = true;
    this.testDataMessage = '';
    this.testDataCreated = [];
    this.testDataHowTo = [];
    this.errorMessage = '';
    this.isLoading = true;

    this.commonService
      .generateFreeformTestData({
        count_per_scenario: this.testDataCountPerScenario,
        days_back: this.testDataDaysBack,
        use_llm: this.testDataUseBedrock,
        scenario_ids: scenarioIds.length ? scenarioIds : undefined,
      })
      .subscribe({
        next: (res: any) => {
          this.testDataGenerating = false;
          this.isLoading = false;
          this.preferLiveApiAfterSeed = true;
          this.testDataCreated = res?.created || [];
          this.testDataHowTo = res?.how_to_test || [];
          const mode = res?.use_llm ? 'Bedrock extraction' : 'scripted insights';
          this.testDataMessage = `Seeded ${res?.created_count ?? 0} conversation(s) into RDS via ${mode}. Errors: ${res?.error_count ?? 0}.`;
          // Clear caches and pull live Freeform data from RDS
          this.extractResult = null;
          this.relatedResult = null;
          this.clusterGraphResult = null;
          this.reviewItems = [];
          this.activeReviewItem = null;
          // Stay on the current tab (do not bounce to Extract) and reload it from live RDS.
          this.persistWorkspaceState();
          this.refreshActiveWorkspace();
        },
        error: (error) => {
          this.testDataGenerating = false;
          this.handleError(
            error,
            'E2E seed failed. Need FASTAPI_ENV=dev on analytics backend and Cognito auth.'
          );
        },
      });
  }

  useStaticDummyAgain(): void {
    this.preferLiveApiAfterSeed = false;
    this.testDataMessage = 'Switched to static offline dummy (does not write RDS).';
    this.extract();
  }

  private handleError(error: any, fallback: string): void {
    console.error(fallback, error);
    this.errorMessage = error?.error?.detail || error?.message || fallback;
    this.isLoading = false;
  }

  get activeFieldData(): FreeformFieldExtract | null {
    return this.extractResult?.fields?.[this.activeField] ?? null;
  }

  get filteredFreeForm(): FreeformValueCount[] {
    return this.filterBySearch(this.activeFieldData?.free_form ?? [], (r) => r.value);
  }

  get filteredClustered(): FreeformValueCount[] {
    return this.filterBySearch(this.activeFieldData?.clustered ?? [], (r) => r.value);
  }

  get filteredMapping(): FreeformClusterMapping[] {
    return this.filterBySearch(
      this.activeFieldData?.mapping ?? [],
      (r) => `${r.cluster_value} ${r.free_form_value}`
    );
  }

  get allRowsColumns(): string[] {
    if (!this.extractResult?.rows?.length) {
      return [
        'ConversationId',
        'ContactId',
        'InitiationTimestamp',
        'Summary',
        'PrimaryTopic',
        'Intent',
        'Sentiment',
        'JourneyMoment',
        'CallerType',
        'Resolution',
        ...this.pairedFieldTabs.flatMap((t) => [t.key, `${t.key}Clustered`]),
      ];
    }
    return Object.keys(this.extractResult.rows[0]);
  }

  get filteredAllRows(): Record<string, string | number>[] {
    const rows = this.extractResult?.rows ?? [];
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) =>
      Object.values(row).some((v) => String(v ?? '').toLowerCase().includes(term))
    );
  }

  get visibleExtractCount(): number {
    if (!this.extractResult) return 0;
    if (this.extractResult.mode === 'all') {
      return this.extractResult.total_rows ?? this.filteredAllRows.length;
    }
    return this.extractActiveListTotal;
  }

  get extractActiveListTotal(): number {
    const data = this.activeFieldData;
    if (!data) return 0;
    if (this.activeView === 'free_form') {
      return data.free_form_total ?? data.free_form?.length ?? 0;
    }
    if (this.activeView === 'clustered') {
      return data.clustered_total ?? data.clustered?.length ?? 0;
    }
    return data.mapping_total ?? data.mapping?.length ?? 0;
  }

  get extractTotalPages(): number {
    if (!this.extractResult) return 1;
    if (this.extractResult.mode === 'all') {
      const total = this.extractResult.total_rows ?? this.extractResult.row_count ?? 0;
      return Math.max(1, Math.ceil(total / this.extractPageSize));
    }
    return Math.max(1, Math.ceil(this.extractActiveListTotal / this.extractPageSize));
  }

  get extractPageLabel(): string {
    const total = this.extractTotalPages;
    return `Page ${this.extractPage} of ${total}`;
  }

  get showExtractPager(): boolean {
    return !!this.extractResult && this.extractTotalPages > 1;
  }

  get relatedRows(): RelatedMatch[] {
    return this.filterBySearch(
      this.relatedResult?.matches ?? [],
      (r) => `${r.field_label} ${r.value} ${r.matched_tokens.join(' ')}`
    );
  }

  private filterBySearch<T>(rows: T[], textFn: (row: T) => string): T[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => textFn(row).toLowerCase().includes(term));
  }

  runClusterGraph(): void {
    if (!this.startDate || !this.endDate) {
      this.errorMessage = 'Please select a from and to date.';
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';

    if (this.useDummyData) {
      this.withDummyDelay(() => {
        this.clusterGraphResult = buildDummyClusterGraph(
          this.startDate,
          this.endDate,
          this.sharedDataService.selectedTimezoneValue || 'EST'
        );
        this.isLoading = false;
        this.renderNetworkGraph();
      });
      return;
    }

    this.commonService
      .getFreeformClusterGraph({
        ...this.dateBody(),
        min_edge_count: 2,
        // No per-cluster / candidate caps — return full graph + all candidates.
        candidate_min_count: 5,
        fields: this.clusterMapField === 'ALL' ? undefined : [this.clusterMapField],
      })
      .subscribe({
        next: (response: ClusterGraphResponse) => {
          this.clusterGraphResult = response;
          this.isLoading = false;
          this.renderNetworkGraph();
        },
        error: (error) => this.handleError(error, 'Failed to build cluster graph.'),
      });
  }

  loadClusterReview(opts?: { preserveSelection?: boolean; forceRescore?: boolean }): void {
    if (!this.startDate || !this.endDate) {
      this.errorMessage = 'Please select a from and to date.';
      return;
    }
    if (opts?.preserveSelection && this.activeReviewItem) {
      this.pendingReviewSelectionKey = this.reviewItemKey(this.activeReviewItem);
    }
    this.isLoading = true;
    this.errorMessage = '';
    if (!opts?.preserveSelection) {
      this.reviewActionMessage = '';
    }
    this.reviewPerformanceNote = '';
    this.activeReviewItem = null;
    this.reviewWindowDays = this.getSelectedRangeDays();

    // Long windows (3 weeks / 3 months): one field at a time is much faster.
    // Do not override an explicit field the user already chose / restored.
    if (this.reviewWindowDays >= 21 && this.reviewFieldFilter === 'ALL') {
      this.reviewFieldFilter = 'Topics';
      this.reviewActionMessage =
        'Long date range: switched field search to Topics for speed. Change field pills to review Root Cause / Outcome / etc.';
      this.persistWorkspaceState();
    }
    // Keep closeness % on — scores are required for Assign / Create / Mark Other.
    // Backend serves cached scores first; only cache misses are re-embedded (unless force rescore).
    this.reviewIncludeScores = true;

    if (this.useDummyData) {
      this.withDummyDelay(() => {
        this.reviewItems = buildDummyClusterReviewItems();
        this.reviewPolicy = {
          auto_assign_threshold: 0.75,
          review_threshold: 0.55,
        };
        this.isLoading = false;
        this.restoreOrOpenReviewItem();
      });
      return;
    }

    const scoreBuckets = new Set([
      'auto_assign',
      'human_review',
      'create_new_cluster',
      'mark_other',
    ]);
    const includeScores =
      this.reviewIncludeScores || scoreBuckets.has(this.reviewBucket);
    const forceRescore = !!opts?.forceRescore;

    this.commonService
      .getClusterReviewQueue({
        ...this.dateBody(),
        bucket: this.reviewBucket,
        // No limit — every distinct free-form in range; score hydration stays budgeted server-side.
        // min_count=1: 15k/day is volume context only, not a reason to hide rare values.
        min_count: 1,
        include_scores: includeScores,
        force_rescore: forceRescore,
        fields: this.reviewFieldFilter === 'ALL' ? undefined : [this.reviewFieldFilter],
      })
      .subscribe({
        next: (response: any) => {
          this.reviewItems = response?.items || [];
          this.reviewPolicy = response?.policy || null;
          this.reviewWindowDays = response?.window_days || this.reviewWindowDays;
          this.reviewMinCount =
            response?.min_count != null ? Number(response.min_count) : null;
          this.reviewPerformanceNote = response?.performance_note || '';
          this.reviewScoreCoverage = response?.score_coverage || [];
          this.reviewThisWindowCoverage = response?.this_window_coverage || [];
          this.reviewCalendarDays = {
            ...this.reviewCalendarDays,
            ...(response?.calendar_days || {}),
          };
          this.isLoading = false;
          if (forceRescore) {
            this.reviewActionMessage =
              `Reprocessed closeness % — ${response?.scored_in_request || 0} re-embedded` +
              (response?.scores_from_cache ? ` · ${response.scores_from_cache} still from cache` : '') +
              '.';
          } else if (response?.performance_note) {
            this.reviewActionMessage = response.performance_note;
          } else if (opts?.preserveSelection) {
            this.reviewActionMessage = `Values refreshed · ${this.reviewItems.length} item(s) · field ${this.reviewFieldFilter}`;
          } else if (response?.scores_from_cache || response?.scored_in_request) {
            this.reviewActionMessage =
              `Closeness % ready — ${response.scores_from_cache || 0} from cache, ` +
              `${response.scored_in_request || 0} newly scored. Use % to Assign / Create / Mark Other.`;
          }
          this.persistWorkspaceState();
          this.restoreOrOpenReviewItem();
        },
        error: (error) => this.handleError(error, 'Failed to load cluster review values.'),
      });
  }

  /** Fetch scored date windows from DB (no Bedrock) — helps avoid re-scoring known ranges. */
  loadClusterReviewScoreCoverage(): void {
    if (this.useDummyData || !this.startDate || !this.endDate) return;
    if (this.endDate) {
      this.reviewCalendarMonth = dayjs(this.endDate).startOf('month');
    }
    this.commonService
      .getClusterReviewScoreCoverage({
        ...this.dateBody(),
        fields: this.reviewFieldFilter === 'ALL' ? undefined : [this.reviewFieldFilter],
        limit: 200,
      })
      .subscribe({
        next: (res: any) => {
          this.reviewScoreCoverage = res?.windows || [];
          this.reviewCalendarDays = {
            ...this.reviewCalendarDays,
            ...(res?.calendar_days || {}),
          };
        },
        error: () => {
          /* non-blocking — coverage is advisory */
        },
      });
  }

  formatCoverageWindow(row: { start_date: string; end_date: string }): string {
    if (!row?.start_date) return '';
    if (row.start_date === row.end_date) return row.start_date;
    return `${row.start_date} → ${row.end_date}`;
  }

  get reviewCoverageSummary(): string {
    const days = Object.keys(this.reviewCalendarDays || {});
    if (!days.length) return '';
    const complete = days.filter((d) => this.reviewCalendarDays[d]?.status === 'complete').length;
    const partial = days.filter((d) => this.reviewCalendarDays[d]?.status === 'partial').length;
    const field =
      this.reviewFieldFilter === 'ALL' ? 'all fields' : this.reviewFieldFilter;
    return `${complete} day(s) scored` + (partial ? ` · ${partial} partial` : '') + ` · ${field}`;
  }

  get reviewCalendarMonthLabel(): string {
    return this.reviewCalendarMonth.format('MMMM YYYY');
  }

  get reviewCalendarCells(): Array<{
    date: string | null;
    dayNum: number | null;
    inMonth: boolean;
    status: 'none' | 'complete' | 'partial';
    selected: boolean;
    inRange: boolean;
    isToday: boolean;
  }> {
    const month = this.reviewCalendarMonth.startOf('month');
    const startPad = month.day(); // 0=Sun
    const daysInMonth = month.daysInMonth();
    const today = dayjs().format('YYYY-MM-DD');
    const rangeStart = this.startDate;
    const rangeEnd = this.endDate;
    const cells: Array<{
      date: string | null;
      dayNum: number | null;
      inMonth: boolean;
      status: 'none' | 'complete' | 'partial';
      selected: boolean;
      inRange: boolean;
      isToday: boolean;
    }> = [];
    for (let i = 0; i < startPad; i++) {
      cells.push({
        date: null,
        dayNum: null,
        inMonth: false,
        status: 'none',
        selected: false,
        inRange: false,
        isToday: false,
      });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const date = month.date(d).format('YYYY-MM-DD');
      const cov = this.reviewCalendarDays[date];
      const status =
        cov?.status === 'complete' ? 'complete' : cov?.status === 'partial' ? 'partial' : 'none';
      const inRange = !!(rangeStart && rangeEnd && date >= rangeStart && date <= rangeEnd);
      cells.push({
        date,
        dayNum: d,
        inMonth: true,
        status,
        selected: this.reviewCalendarSelectedDay === date,
        inRange,
        isToday: date === today,
      });
    }
    while (cells.length % 7 !== 0) {
      cells.push({
        date: null,
        dayNum: null,
        inMonth: false,
        status: 'none',
        selected: false,
        inRange: false,
        isToday: false,
      });
    }
    return cells;
  }

  shiftReviewCalendarMonth(delta: number): void {
    this.reviewCalendarMonth = this.reviewCalendarMonth.add(delta, 'month').startOf('month');
  }

  selectCoverageCalendarDay(date: string | null): void {
    if (!date) return;
    this.reviewCalendarSelectedDay = date;
  }

  /** Set page date range to the selected calendar day and Load (uses cache when available). */
  loadSelectedCoverageDay(): void {
    if (!this.reviewCalendarSelectedDay) {
      this.errorMessage = 'Select a day on the coverage calendar first.';
      return;
    }
    this.startDate = this.reviewCalendarSelectedDay;
    this.endDate = this.reviewCalendarSelectedDay;
    this.persistWorkspaceState();
    this.loadClusterReview({ preserveSelection: false, forceRescore: false });
  }

  /** Re-embed closeness % for the selected day (ignores score cache). */
  reprocessSelectedCoverageDay(): void {
    if (!this.reviewCalendarSelectedDay) {
      this.errorMessage = 'Select a day on the coverage calendar first.';
      return;
    }
    this.startDate = this.reviewCalendarSelectedDay;
    this.endDate = this.reviewCalendarSelectedDay;
    this.persistWorkspaceState();
    this.loadClusterReview({ preserveSelection: false, forceRescore: true });
  }

  /** Re-embed closeness % for the current page date range. */
  reprocessCurrentCoverageRange(): void {
    if (!this.startDate || !this.endDate) {
      this.errorMessage = 'Please select a from and to date.';
      return;
    }
    this.loadClusterReview({ preserveSelection: true, forceRescore: true });
  }

  /** Re-open prior selection after refresh, else first queue row. */
  restoreOrOpenReviewItem(): void {
    const key = this.pendingReviewSelectionKey;
    this.pendingReviewSelectionKey = null;
    if (key) {
      const match = this.filteredReviewItems.find((r) => this.reviewItemKey(r) === key);
      if (match) {
        this.openReviewItem(match);
        return;
      }
    }
    const first = this.filteredReviewItems[0];
    if (first) this.openReviewItem(first);
  }

  /** @deprecated use restoreOrOpenReviewItem */
  autoOpenFirstReviewItem(): void {
    this.restoreOrOpenReviewItem();
  }

  /** Best-known cluster for a queue row: closest match → predicted → assigned. */
  reviewItemMappedCluster(row: ClusterReviewItem): string {
    const closest = row?.closest_clusters?.[0]?.cluster_name?.trim();
    if (closest) return closest;
    const predicted = (row?.predicted_cluster_name || '').trim();
    if (predicted) return predicted;
    const assigned = (row?.assigned_cluster_name || '').trim();
    if (assigned && !['other', 'others', 'na', 'n/a'].includes(assigned.toLowerCase())) {
      return assigned;
    }
    return assigned || '—';
  }

  reviewItemMappedClusterIsCatchall(row: ClusterReviewItem): boolean {
    const name = this.reviewItemMappedCluster(row).toLowerCase();
    return !name || name === '—' || ['other', 'others', 'na', 'n/a'].includes(name);
  }

  /** Inclusive day count for the selected Freeform date range. */
  getSelectedRangeDays(): number {
    if (!this.startDate || !this.endDate) return 0;
    const start = dayjs(this.startDate);
    const end = dayjs(this.endDate);
    return Math.max(1, end.diff(start, 'day') + 1);
  }

  /**
   * Quick ranges for cluster review (3 weeks / 3 months).
   * Uses the page date picker values and reloads the active workspace.
   */
  applyReviewRangePreset(days: number): void {
    const end = dayjs();
    const start = end.subtract(Math.max(1, days) - 1, 'day');
    this.endDate = end.format('YYYY-MM-DD');
    this.startDate = start.format('YYYY-MM-DD');
    this.extractResult = null;
    this.relatedResult = null;
    this.clusterGraphResult = null;
    this.reviewItems = [];
    this.activeReviewItem = null;
    this.reviewIncludeScores = true;
    // Keep queue / field filters — do not reset them on range preset.
    this.persistWorkspaceState();
    if (this.workspaceTab === 'cluster-review') {
      this.loadClusterReviewScoreCoverage();
      this.loadClusterReview({ preserveSelection: false });
    } else {
      this.refreshActiveWorkspace();
    }
  }

  openReviewItem(item: ClusterReviewItem): void {
    this.activeReviewItem = item;
    this.pendingReviewSelectionKey = this.reviewItemKey(item);
    this.persistWorkspaceState();
    // Keep the score calendar open; only close Help / Seed so the action panel stays usable.
    if (this.rightDrawer === 'help' || this.rightDrawer === 'seed') {
      this.closeRightDrawer();
    }
    this.reviewActionMessage = '';
    this.reviewLastCreatedCluster = null;
    this.reviewSelectedClusterName = item.closest_clusters?.[0]?.cluster_name || '';
    this.reviewNewClusterName = item.free_form_value?.slice(0, 80) || '';
    // Pick a sensible default action from the policy recommendation
    const action = item.recommendation?.action || '';
    if (action === 'create_new_cluster') this.reviewActionMode = 'create';
    else if (action === 'mark_other') this.reviewActionMode = 'other';
    else this.reviewActionMode = 'assign';
    this.reviewConversations = [];
    this.reviewConversationsTotal = 0;
    this.reviewConversationsTruncated = false;
    this.selectedConversationKeys = {};
    if (this.useDummyData) {
      this.reviewClusterOptions = this.mergeClusterOptions(
        (item.closest_clusters || []).map((c) => ({
          cluster_id: c.cluster_id,
          cluster_name: c.cluster_name,
          mechanism: c.mechanism || 'human_in_loop',
        })),
        item.closest_clusters
      );
      if (!this.reviewClusterOptions.length) {
        this.reviewClusterOptions = [
          { cluster_id: 101, cluster_name: 'Travel Disruption', mechanism: 'dummy' },
          { cluster_id: 102, cluster_name: 'Loyalty Program', mechanism: 'dummy' },
          { cluster_id: 103, cluster_name: 'Baggage', mechanism: 'dummy' },
        ];
      }
      if (!this.reviewSelectedClusterName && this.reviewClusterOptions.length) {
        this.selectReviewCluster(this.reviewClusterOptions[0].cluster_name);
      }
    } else {
      this.commonService.listClusterReviewClusters({ field: item.field }).subscribe({
        next: (res: any) => {
          this.reviewClusterOptions = this.mergeClusterOptions(
            res?.clusters || [],
            item.closest_clusters
          );
          if (this.reviewSelectedClusterName) {
            this.selectReviewCluster(this.reviewSelectedClusterName);
          } else if (this.reviewClusterOptions.length) {
            this.selectReviewCluster(this.reviewClusterOptions[0].cluster_name);
          }
        },
        error: () => {
          this.reviewClusterOptions = this.mergeClusterOptions([], item.closest_clusters);
        },
      });
      // Score one row at a time (fast queue load); avoid bulk embed on Load review.
      if (item.similarity_pct == null && !item.compare_error) {
        this.recompareActiveReview();
      }
    }
    this.loadReviewConversations(item);
  }

  loadReviewConversations(item: ClusterReviewItem = this.activeReviewItem!): void {
    if (!item) return;

    const applyConversations = (conversations: any[], totalMatching?: number, truncated?: boolean) => {
      this.reviewConversations = conversations || [];
      this.reviewConversationsTotal =
        totalMatching != null ? Number(totalMatching) : this.reviewConversations.length;
      this.reviewConversationsTruncated = !!truncated;
      this.selectedConversationKeys = {};
      for (const c of this.reviewConversations) {
        const key = this.conversationKey(c);
        this.selectedConversationKeys[key] = true;
      }
    };

    if (this.useDummyData) {
      this.withDummyDelay(() => {
        const rows = buildDummyReviewConversations(item.free_form_value);
        applyConversations(rows, rows.length, false);
      });
      return;
    }

    this.commonService
      .listClusterReviewConversations({
        ...this.dateBody(),
        field: item.field,
        free_form_value: item.free_form_value,
        // No limit — return every matching conversation in range.
      })
      .subscribe({
        next: (res: any) =>
          applyConversations(
            res?.conversations || [],
            res?.total_matching ?? res?.count,
            res?.truncated
          ),
        error: (error) => this.handleError(error, 'Failed to load conversations for review.'),
      });
  }

  conversationKey(c: any): string {
    return `${c.ConversationId || c.conversation_id}||${c.InitiationTimestamp || c.initiation_timestamp}`;
  }

  get selectedReviewConversations(): any[] {
    return this.reviewConversations.filter((c) => this.selectedConversationKeys[this.conversationKey(c)]);
  }

  /** Target cluster for the visible free-form → cluster mapping. */
  get reviewMappingTarget(): string {
    if (this.reviewActionMode === 'create') return (this.reviewNewClusterName || '').trim();
    if (this.reviewActionMode === 'other') {
      return this.activeReviewItem?.field === 'Topics' ? 'Other' : 'NA';
    }
    return (this.reviewSelectedClusterName || '').trim();
  }

  /** Closeness % of the currently selected target cluster vs the free-form (from closest matches). */
  get reviewMappingClosenessPct(): number | null {
    const target = this.reviewMappingTarget;
    if (!target || this.reviewActionMode === 'create' || this.reviewActionMode === 'other') {
      return null;
    }
    const item = this.activeReviewItem;
    const hit = (item?.closest_clusters || []).find(
      (c) => (c.cluster_name || '').trim().toLowerCase() === target.toLowerCase()
    );
    if (!hit) return null;
    if (hit.similarity_pct != null) return Number(hit.similarity_pct);
    if (hit.similarity != null) return Number(hit.similarity) * 100;
    return null;
  }

  get reviewMappingClosenessBand(): 'high' | 'mid' | 'low' | '' {
    const pct = this.reviewMappingClosenessPct;
    if (pct == null) return '';
    if (pct >= 90) return 'high';
    if (pct >= 70) return 'mid';
    return 'low';
  }

  /** Merge catalog + closest so the dropdown always shows the selected/closest names. */
  mergeClusterOptions(
    catalog: { cluster_id: number; cluster_name: string; mechanism: string }[],
    closest?: ClusterReviewClosest[] | null
  ): { cluster_id: number; cluster_name: string; mechanism: string }[] {
    const byName = new Map<string, { cluster_id: number; cluster_name: string; mechanism: string }>();
    for (const c of catalog || []) {
      if (c?.cluster_name) byName.set(c.cluster_name, c);
    }
    for (const c of closest || []) {
      if (!c?.cluster_name || byName.has(c.cluster_name)) continue;
      byName.set(c.cluster_name, {
        cluster_id: c.cluster_id ?? -1,
        cluster_name: c.cluster_name,
        mechanism: c.mechanism || 'embedding',
      });
    }
    const selected = (this.reviewSelectedClusterName || '').trim();
    if (selected && !byName.has(selected)) {
      byName.set(selected, {
        cluster_id: -1,
        cluster_name: selected,
        mechanism: 'selected',
      });
    }
    return Array.from(byName.values()).sort((a, b) =>
      a.cluster_name.localeCompare(b.cluster_name)
    );
  }

  /** Pick a catalog/closest cluster and keep dropdown + mapping in sync. */
  selectReviewCluster(clusterName: string, meta?: { cluster_id?: number; mechanism?: string }): void {
    const name = (clusterName || '').trim();
    this.reviewSelectedClusterName = name;
    if (!name) return;
    if (!this.reviewClusterOptions.some((c) => c.cluster_name === name)) {
      this.reviewClusterOptions = [
        {
          cluster_id: meta?.cluster_id ?? -1,
          cluster_name: name,
          mechanism: meta?.mechanism || 'human_in_loop',
        },
        ...this.reviewClusterOptions,
      ];
    }
  }

  toggleSelectAllReviewConversations(select: boolean): void {
    for (const c of this.reviewConversations) {
      this.selectedConversationKeys[this.conversationKey(c)] = select;
    }
  }

  recompareActiveReview(): void {
    if (!this.activeReviewItem) return;
    const item = this.activeReviewItem;

    if (this.useDummyData) {
      this.withDummyDelay(() => {
        item.best_similarity = 0.71;
        item.similarity_pct = 71;
        item.closest_clusters = [
          {
            cluster_id: 101,
            cluster_name: 'Travel Disruption',
            value_text: 'Travel Disruption',
            similarity: 0.71,
            similarity_pct: 71,
            mechanism: 'embedding',
          },
          {
            cluster_id: 102,
            cluster_name: 'Loyalty Program',
            value_text: 'Loyalty Program',
            similarity: 0.58,
            similarity_pct: 58,
            mechanism: 'embedding',
          },
        ];
        item.recommendation = {
          action: 'human_review',
          similarity_pct: 71,
          label: 'Review (dummy)',
          detail: 'Similarity in review band — confirm or reassign.',
          allow_create_cluster: true,
          allow_mark_other: true,
          allow_reassign: true,
        };
        this.reviewClusterOptions = this.mergeClusterOptions(
          this.reviewClusterOptions,
          item.closest_clusters
        );
        const top = item.closest_clusters?.[0];
        if (top?.cluster_name) {
          this.selectReviewCluster(top.cluster_name, {
            cluster_id: top.cluster_id,
            mechanism: top.mechanism,
          });
        }
        this.reviewActionMessage = 'Recompared with dummy scores.';
      });
      return;
    }

    this.commonService
      .compareClusterReview({
        field: item.field,
        free_form_value: item.free_form_value,
        free_form_count: item.count,
        top_k: 8,
      })
      .subscribe({
        next: (res: any) => {
          item.best_similarity = res.best_similarity;
          item.similarity_pct = res.similarity_pct;
          item.closest_clusters = res.closest_clusters || [];
          item.recommendation = res.recommendation;
          this.reviewClusterOptions = this.mergeClusterOptions(
            this.reviewClusterOptions,
            item.closest_clusters
          );
          const top = item.closest_clusters?.[0];
          if (top?.cluster_name) {
            this.selectReviewCluster(top.cluster_name, {
              cluster_id: top.cluster_id,
              mechanism: top.mechanism,
            });
          }
        },
        error: (error) => this.handleError(error, 'Compare failed.'),
      });
  }

  applySelectedConversationEdits(): void {
    if (!this.activeReviewItem) return;
    const selected = this.selectedReviewConversations;
    if (!selected.length) {
      this.errorMessage = 'Select at least one conversation to update.';
      return;
    }
    const item = this.activeReviewItem;
    const clusterName = this.reviewSelectedClusterName.trim();
    if (!clusterName) {
      this.errorMessage = 'Select or enter a cluster name before saving.';
      return;
    }
    // Cluster review edits ONLY the cluster label.
    const updates: any = { cluster_name: clusterName };
    const closest = (item.closest_clusters || []).find((c) => c.cluster_name === clusterName);
    if (closest?.cluster_id != null) updates.cluster_id = closest.cluster_id;
    else {
      const opt = this.reviewClusterOptions.find((c) => c.cluster_name === clusterName);
      if (opt?.cluster_id != null && opt.cluster_id >= 0) updates.cluster_id = opt.cluster_id;
    }

    if (this.useDummyData) {
      this.isLoading = true;
      this.withDummyDelay(() => {
        this.isLoading = false;
        this.reviewActionMessage = `Updated cluster on ${selected.length} conversation(s) (dummy).`;
        this.refreshClusterReview();
      });
      return;
    }

    this.isLoading = true;
    this.commonService
      .updateClusterReviewConversations({
        field: item.field,
        free_form_value: item.free_form_value,
        conversations: selected.map((c) => ({
          conversation_id: c.ConversationId,
          initiation_timestamp: c.InitiationTimestamp,
        })),
        updates,
        apply_to_upcoming: this.reviewApplyToUpcoming,
        similarity: item.best_similarity,
        notes: 'HITL cluster-only edit from Freeform Cluster review',
      })
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.reviewActionMessage = `Updated cluster → “${clusterName}” on ${res.updated_conversations} conversation(s). Audit: ${(res.audit_ids || []).join(', ') || 'n/a'}${res.catalog_appended ? ' · catalog updated for upcoming' : ''}`;
          if (this.activeReviewItem) {
            this.activeReviewItem.assigned_cluster_name = clusterName;
          }
          this.refreshClusterReview();
        },
        error: (error) => this.handleError(error, 'Failed to update conversations.'),
      });
  }

  applyReassign(): void {
    if (!this.activeReviewItem || !this.reviewSelectedClusterName.trim()) return;
    // Prefer targeted conversation updates when any are selected
    if (this.selectedReviewConversations.length) {
      this.applySelectedConversationEdits();
      return;
    }
    const item = this.activeReviewItem;
    const closest = (item.closest_clusters || []).find(
      (c) => c.cluster_name === this.reviewSelectedClusterName
    );

    if (this.useDummyData) {
      this.isLoading = true;
      this.withDummyDelay(() => {
        this.isLoading = false;
        this.reviewActionMessage = `Reassigned → ${this.reviewSelectedClusterName.trim()} (dummy backfill)`;
        this.refreshClusterReview();
      });
      return;
    }

    this.isLoading = true;
    this.commonService
      .reassignClusterReview({
        ...this.dateBody(),
        field: item.field,
        free_form_value: item.free_form_value,
        cluster_name: this.reviewSelectedClusterName.trim(),
        cluster_id: closest?.cluster_id,
        similarity: closest?.similarity ?? item.best_similarity,
        backfill: true,
        apply_to_upcoming: true,
      })
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.reviewActionMessage = `Reassigned → ${res.cluster_name} (backfill ${res.backfill_rows}, catalog ${res.catalog_appended ? 'updated' : 'skipped'})`;
          this.refreshClusterReview();
        },
        error: (error) => this.handleError(error, 'Reassign failed.'),
      });
  }

  applyCreateCluster(): void {
    if (!this.activeReviewItem || !this.reviewNewClusterName.trim()) return;
    const item = this.activeReviewItem;
    const createdName = this.reviewNewClusterName.trim();
    const selected = this.selectedReviewConversations;

    // Prefer applying only to reviewed/selected conversations.
    if (!selected.length) {
      this.errorMessage =
        'Select the conversations below to review, then create the cluster. Use “Bulk create in date range” only if you intend to update every match.';
      return;
    }

    if (this.useDummyData) {
      this.isLoading = true;
      this.withDummyDelay(() => {
        this.reviewLastCreatedCluster = createdName;
        this.reviewSelectedClusterName = createdName;
        this.reviewActionMode = 'assign';
        item.assigned_cluster_name = createdName;
        this.applySelectedConversationEdits();
      });
      return;
    }

    this.isLoading = true;
    this.commonService
      .createClusterReview({
        ...this.dateBody(),
        field: item.field,
        free_form_value: item.free_form_value,
        cluster_name: createdName,
        similarity: item.best_similarity,
        // Do not silent-bulk the whole window — apply to selected rows next.
        backfill: false,
      })
      .subscribe({
        next: (res: any) => {
          const name = res.cluster_name || createdName;
          const id = res.cluster_id;
          this.reviewLastCreatedCluster = name;
          this.reviewSelectedClusterName = name;
          this.reviewActionMode = 'assign';
          if (!this.reviewClusterOptions.some((c) => c.cluster_name === name)) {
            this.reviewClusterOptions = [
              { cluster_id: id ?? -1, cluster_name: name, mechanism: 'human_in_loop' },
              ...this.reviewClusterOptions,
            ];
          }
          item.assigned_cluster_name = name;
          // Apply the new cluster to the conversations the analyst reviewed.
          this.applySelectedConversationEdits();
        },
        error: (error) => this.handleError(error, 'Create cluster failed.'),
      });
  }

  /** Create cluster + backfill every matching free-form in the date range (explicit bulk). */
  applyCreateClusterBulk(): void {
    if (!this.activeReviewItem || !this.reviewNewClusterName.trim()) return;
    const item = this.activeReviewItem;
    const createdName = this.reviewNewClusterName.trim();
    this.isLoading = true;
    this.commonService
      .createClusterReview({
        ...this.dateBody(),
        field: item.field,
        free_form_value: item.free_form_value,
        cluster_name: createdName,
        similarity: item.best_similarity,
        backfill: true,
      })
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          const name = res.cluster_name || createdName;
          const id = res.cluster_id;
          this.reviewLastCreatedCluster = name;
          this.reviewSelectedClusterName = name;
          this.reviewActionMode = 'assign';
          if (!this.reviewClusterOptions.some((c) => c.cluster_name === name)) {
            this.reviewClusterOptions = [
              { cluster_id: id ?? -1, cluster_name: name, mechanism: 'human_in_loop' },
              ...this.reviewClusterOptions,
            ];
          }
          this.reviewActionMessage = `Created cluster “${name}”${id != null ? ` (id ${id})` : ''} — bulk backfill ${res.backfill_rows || 0} row(s) in date range · catalog updated for upcoming.`;
          item.assigned_cluster_name = name;
          this.loadReviewConversations(item);
        },
        error: (error) => this.handleError(error, 'Create cluster failed.'),
      });
  }

  applyMarkOther(): void {
    if (!this.activeReviewItem) return;
    const item = this.activeReviewItem;
    this.isLoading = true;
    this.commonService
      .markOtherClusterReview({
        ...this.dateBody(),
        field: item.field,
        free_form_value: item.free_form_value,
        similarity: item.best_similarity,
        backfill: true,
      })
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.reviewActionMessage = `Marked as ${res.cluster_name}. Search Other later to reassign.`;
          this.refreshClusterReview();
        },
        error: (error) => this.handleError(error, 'Mark Other failed.'),
      });
  }

  get filteredReviewItems(): ClusterReviewItem[] {
    return this.filterBySearch(
      this.reviewItems,
      (r) => `${r.field} ${r.free_form_value} ${r.assigned_cluster_name} ${r.recommendation?.label || ''}`
    );
  }

  get clusterCandidates(): NewClusterCandidate[] {
    const rows = this.clusterGraphResult?.new_cluster_candidates ?? [];
    return this.filterBySearch(
      rows,
      (r) => `${r.field_label} ${r.free_form_value} ${r.suggested_cluster_name} ${r.current_cluster}`
    );
  }

  get clusterGroups(): SuggestedClusterGroup[] {
    return this.clusterGraphResult?.suggested_cluster_groups ?? [];
  }

  renderNetworkGraph(): void {
    const graph = this.clusterGraphResult?.graph;
    if (!graph) {
      this.networkChartOptions = {};
      return;
    }
    const nodes = (graph.nodes || []).map((n) => ({
      id: n.id,
      name: n.name,
      color: n.type === 'cluster' ? '#6a4ff5' : '#94a3b8',
      marker: { radius: n.marker?.radius || (n.type === 'cluster' ? 16 : 8) },
      custom: {
        type: n.type,
        field: n.field_label,
        count: n.count,
        cluster: n.cluster,
      },
    }));
    const data = (graph.links || []).map((l) => ({
      from: l.from,
      to: l.to,
      weight: Math.max(l.weight, 1),
    }));

    this.networkChartOptions = {
      chart: { type: 'networkgraph', height: 560, backgroundColor: 'transparent' },
      title: { text: undefined },
      credits: { enabled: false },
      tooltip: {
        useHTML: true,
        formatter: function () {
          const p: any = this.point;
          if (p.fromNode && p.toNode) {
            return `<b>${p.fromNode.name}</b> → <b>${p.toNode.name}</b><br/>Weight: ${p.weight || ''}`;
          }
          const c = p.options?.custom || {};
          return `<b>${p.name}</b><br/>${c.type || ''}${c.field ? ' · ' + c.field : ''}<br/>Count: ${c.count ?? ''}`;
        },
      },
      plotOptions: {
        networkgraph: {
          keys: ['from', 'to'],
          layoutAlgorithm: {
            enableSimulation: true,
            friction: -0.9,
            linkLength: 40,
          },
          dataLabels: {
            enabled: true,
            linkFormat: '',
            allowOverlap: false,
            style: { textOutline: 'none', fontSize: '10px' },
          },
        },
      },
      series: [
        {
          type: 'networkgraph',
          name: 'Cluster ↔ Free-form',
          data,
          nodes,
        } as Highcharts.SeriesNetworkgraphOptions,
      ],
    };
    this.updateNetworkFlag = !this.updateNetworkFlag;
  }

  openDownloadOptions(): void {
    if (!this.canDownload) return;
    this.downloadScope = 'current_view';
    this.applyCurrentViewToDownload();
    this.showDownloadOptions = true;
  }

  closeDownloadOptions(): void {
    this.showDownloadOptions = false;
  }

  setDownloadScope(scope: 'selection' | 'current_view'): void {
    this.downloadScope = scope;
    if (scope === 'current_view') this.applyCurrentViewToDownload();
  }

  private syncDownloadPanelToCurrentView(): void {
    if (this.showDownloadOptions && this.downloadScope === 'current_view') {
      this.applyCurrentViewToDownload();
    }
  }

  private applyCurrentViewToDownload(): void {
    const allFieldsOnScreen =
      this.extractResult?.mode === 'all' && this.extractFieldFilter === 'ALL';
    const currentField =
      this.extractFieldFilter === 'ALL' ? this.activeField : this.extractFieldFilter;
    (Object.keys(this.downloadFields) as FreeformFieldKey[]).forEach((k) => {
      this.downloadFields[k] = allFieldsOnScreen ? true : k === currentField;
    });
    if (this.extractResult?.mode === 'unique') {
      this.downloadLayers = {
        free_form: this.activeView === 'free_form',
        clustered: this.activeView === 'clustered',
        mapping: this.activeView === 'mapping',
      };
    }
  }

  toggleDownloadField(field: FreeformFieldKey): void {
    if (this.downloadScope === 'current_view') this.downloadScope = 'selection';
    this.downloadFields[field] = !this.downloadFields[field];
  }

  selectAllDownloadFields(selected: boolean): void {
    if (this.downloadScope === 'current_view') this.downloadScope = 'selection';
    (Object.keys(this.downloadFields) as FreeformFieldKey[]).forEach((k) => {
      this.downloadFields[k] = selected;
    });
  }

  onDownloadLayerChange(): void {
    if (this.downloadScope === 'current_view') this.downloadScope = 'selection';
  }

  get selectedDownloadFieldKeys(): FreeformFieldKey[] {
    return (Object.keys(this.downloadFields) as FreeformFieldKey[]).filter(
      (k) => this.downloadFields[k]
    );
  }

  get hasClusteredDownloadFields(): boolean {
    return this.selectedDownloadFieldKeys.some((k) => this.fieldHasClustered(k));
  }

  get downloadOptionsValid(): boolean {
    if (this.workspaceTab !== 'extract' || !this.extractResult) return false;
    const fields = this.selectedDownloadFieldKeys;
    if (!fields.length) return false;
    if (this.extractResult.mode === 'all') return true;
    const clustered = this.hasClusteredDownloadFields;
    return (
      this.downloadLayers.free_form ||
      (clustered && (this.downloadLayers.clustered || this.downloadLayers.mapping))
    );
  }

  get downloadPreviewLabel(): string {
    const labels = this.selectedDownloadFieldKeys.map(
      (k) => this.fieldTabs.find((t) => t.key === k)?.label || k
    );
    const fieldText = labels.length ? labels.join(', ') : 'No fields';
    if (this.extractResult?.mode === 'all') {
      return `CSV will include conversation rows for: ${fieldText}`;
    }
    const layers: string[] = [];
    if (this.downloadLayers.free_form) layers.push('Free-form');
    if (this.hasClusteredDownloadFields && this.downloadLayers.clustered) layers.push('Clustered');
    if (this.hasClusteredDownloadFields && this.downloadLayers.mapping) layers.push('Mapping');
    return `CSV will include ${layers.join(' + ') || 'no layers'} for: ${fieldText}`;
  }

  get downloadHint(): string {
    if (this.extractResult?.mode === 'all') {
      return this.downloadScope === 'current_view'
        ? 'Exports every matching conversation row for the field(s) on screen (not just this page). Identity columns and Summary are included so each row is identifiable.'
        : 'Exports every matching conversation row for the fields you check (not just this page). Identity columns and Summary are included so each row is identifiable.';
    }
    return this.downloadScope === 'current_view'
      ? 'Exports every unique value for the field and view on screen (not just this page).'
      : 'Exports every unique value for the fields and layers you check (not just this page).';
  }

  /** Opens options panel instead of immediate download. */
  exportCsv(): void {
    this.openDownloadOptions();
  }

  confirmDownload(): void {
    if (!this.downloadOptionsValid) return;
    this.exportExtractWithOptions();
  }

  private exportExtractWithOptions(): void {
    if (!this.extractResult) return;
    const fields = this.selectedDownloadFieldKeys;
    const mode = this.extractResult.mode;

    const finish = (full: FreeformCollectionExtractResponse) => {
      this.extractDownloadBusy = false;
      this.writeExtractCsv(full, fields);
      this.closeDownloadOptions();
    };

    this.extractDownloadBusy = true;
    this.errorMessage = '';

    if (this.useDummyData) {
      this.withDummyDelay(() => {
        finish(
          buildDummyExtract(
            this.startDate,
            this.endDate,
            this.sharedDataService.selectedTimezoneValue || 'EST',
            mode
          )
        );
      });
      return;
    }

    // Re-fetch the full (unpaginated) dataset so CSV is not limited to the UI page.
    this.commonService
      .extractFreeformCollection(
        this.buildExtractRequestBody({
          paginated: false,
          fields,
          mode,
        })
      )
      .subscribe({
        next: finish,
        error: (error) => {
          this.extractDownloadBusy = false;
          this.handleError(error, 'Failed to download extract.');
        },
      });
  }

  private writeExtractCsv(
    data: FreeformCollectionExtractResponse,
    fields: FreeformFieldKey[]
  ): void {
    const escape = (v: string | number) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines: string[] = [];

    if (data.mode === 'all') {
      const rows = data.rows ?? [];
      const cols = this.allRowsExportColumns(fields, rows);
      lines.push(cols.join(','));
      for (const row of rows) {
        lines.push(cols.map((c) => escape(row[c] ?? '')).join(','));
      }
      this.downloadCsv(
        lines,
        `freeform-all-rows_${this.downloadFieldFilePart(fields)}_${this.startDate}_to_${this.endDate}.csv`
      );
      return;
    }

    const includeClustered = this.hasClusteredDownloadFields && this.downloadLayers.clustered;
    const includeMapping = this.hasClusteredDownloadFields && this.downloadLayers.mapping;
    lines.push('field,type,cluster_value,value,count');
    for (const field of fields) {
      const block = data.fields?.[field];
      if (!block) continue;
      const clusteredField = this.fieldHasClustered(field);
      if (this.downloadLayers.free_form) {
        for (const row of block.free_form ?? []) {
          lines.push([field, 'free_form', '', escape(row.value), row.count].join(','));
        }
      }
      if (includeClustered && clusteredField) {
        for (const row of block.clustered ?? []) {
          lines.push([field, 'clustered', '', escape(row.value), row.count].join(','));
        }
      }
      if (includeMapping && clusteredField) {
        for (const row of block.mapping ?? []) {
          lines.push(
            [
              field,
              'mapping',
              escape(row.cluster_value),
              escape(row.free_form_value),
              row.count,
            ].join(',')
          );
        }
      }
    }
    const layerPart = [
      this.downloadLayers.free_form ? 'freeform' : '',
      includeClustered ? 'clustered' : '',
      includeMapping ? 'mapping' : '',
    ]
      .filter(Boolean)
      .join('-');
    this.downloadCsv(
      lines,
      `freeform-unique_${this.downloadFieldFilePart(fields)}_${layerPart}_${this.startDate}_to_${this.endDate}.csv`
    );
  }

  private allRowsExportColumns(
    fields: FreeformFieldKey[],
    rows: Record<string, string | number>[]
  ): string[] {
    const cols = ['ConversationId', 'ContactId', 'InitiationTimestamp', 'Summary'];
    for (const field of fields) {
      if (!cols.includes(field)) cols.push(field);
      if (this.fieldHasClustered(field)) {
        const clustered = `${field}Clustered`;
        if (!cols.includes(clustered)) cols.push(clustered);
      }
    }
    if (!rows.length) return cols;
    return cols.filter((c) => c in rows[0] || ['ConversationId', 'ContactId', 'InitiationTimestamp', 'Summary'].includes(c));
  }

  private downloadFieldFilePart(fields: FreeformFieldKey[]): string {
    if (fields.length === this.fieldTabs.length) return 'all-fields';
    return fields.join('-') || 'fields';
  }

  private downloadCsv(lines: string[], filename: string): void {
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  }
}
